import { ReactNode, FC, useMemo } from "react";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import * as Color from "../Color";
import { DataId } from "../DataId";
import classNames from "classnames";
import styled from "styled-components";
import { Handle } from "./Handle";
import { ItemContent } from "./ItemContent";
import { Icon } from "../Icon";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/** The `ListItem` component properties. */
interface ListItemProps {

  /** The list item ID. */
  readonly id: DataId;

  /** The item children. */
  readonly children?: ReactNode;
}

const StyledListItem = styled.li`
  box-sizing: border-box;
  background-color: ${Color.toString(Color.White)};
  border-bottom: solid 1px ${Color.toString(Color.LightGrey[200])};
  padding-top: calc(0.5rem + 1px);
  padding-left: calc(0.5rem + 1px);
  padding-right: calc(0.5rem + 1px);
  padding-bottom: 0.5rem;
  margin: 0;
  list-style: none;

  &.dragging {
    opacity: 0.3;
  }
`;

/** An individual draggable item within the orderable list. */
export const ListItem: FC<ListItemProps> = (props) => {
  const { id, children } = props;
  const {
    setNodeRef,
    setActivatorNodeRef,
    isDragging,
    listeners,
    transform,
    transition,
    attributes,
  } = useSortable({
    id: id.toString(),
    transition: { duration: 0, easing: "linear" },
  });

  const style = useMemo(() => {
    return {
      transform: CSS.Translate.toString(transform),
      transition,
    };
  }, [transform, transition]);

  const { role: _role, tabIndex: _tabIndex, ...restAttributes } = attributes;

  return (
    <StyledListItem
      ref={setNodeRef}
      className={classNames("item", { "dragging": isDragging })}
      style={style}
      {...restAttributes}
      aria-roledescription="sortable item"
      aria-disabled={isDragging ? "true" : "false"}
      >
      <Shelf spacing="small">
        <Handle aria-label="Rearrange element" ref={setActivatorNodeRef} {...listeners}>
          <Icon aria-hidden="true" name="grabber" size="regular" color={Color.LightGrey[500]} />
        </Handle>
        <ItemContent>
          {children}
        </ItemContent>
      </Shelf>
    </StyledListItem>
  );
};
